import React, { useState } from "react";
import { Card, Form, Input, Button, message, Modal, Alert, Typography } from "antd";
import axios from "../api/axios";

const { Title, Text } = Typography;

const API_URL = "/auth";

const COLORS = {
  primary: "#3FB6C2",
  primaryDark: "#1F4E57",
  primaryLight: "#E8F7F9",
  pageBg: "#F4FBFC",
  border: "#DCE7F3",
  text: "#1F2937",
  muted: "#6B7280",
};

const getErrorMessage = (err, fallback) => {
  const data = err?.response?.data;

  if (typeof data === "string" && data.trim()) {
    return data;
  }

  if (data?.message) {
    return data.message;
  }

  if (data?.error) {
    return data.error;
  }

  return fallback;
};

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [pwResetVisible, setPwResetVisible] = useState(false);
  const [pwResetLoading, setPwResetLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [pwResetStep, setPwResetStep] = useState(1);
  const [pwResetCode, setPwResetCode] = useState("");
  const [pwResetNew, setPwResetNew] = useState("");
  const [pwResetNew2, setPwResetNew2] = useState("");
  const [pwResetHint, setPwResetHint] = useState(false);
  const [twoFactorStep, setTwoFactorStep] = useState(false);
  const [twoFactorEmail, setTwoFactorEmail] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [loginError, setLoginError] = useState("");
  const [twoFactorError, setTwoFactorError] = useState("");

  const completeLogin = async (token) => {
    localStorage.setItem("token", token);

    try {
      const userRes = await axios.get("/users/me", {
        headers: { Authorization: "Bearer " + token },
      });
      localStorage.setItem("user", JSON.stringify(userRes.data));
    } catch (e) {
      message.error("Userdaten konnten nicht geladen werden!");
      localStorage.removeItem("token");
      return;
    }

    message.success("Login erfolgreich!");
    setTimeout(() => {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      window.location.assign(user.roles?.some(role => ["DRIVER", "ROLE_DRIVER"].includes(role)) ? "/driver" : "/app/mobile");
    }, 600);
  };

  const onFinish = async (values) => {
    setLoading(true);
    setLoginError("");
    setTwoFactorError("");
    try {
      const response = await axios.post(`${API_URL}/login`, values);

      if (response.data.needTwoFactor) {
        setTwoFactorEmail(values.email);
        setTwoFactorCode("");
        setTwoFactorError("");
        setTwoFactorStep(true);
        message.success("Login-Code wurde per E-Mail gesendet.");
      } else if (response.data.token) {
        await completeLogin(response.data.token);
      } else {
        setLoginError("Login fehlgeschlagen (kein Token erhalten).");
      }
    } catch (err) {
      const errorMessage = getErrorMessage(
        err,
        "Login fehlgeschlagen. Bitte prüfe E-Mail und Passwort."
      );
      setLoginError(errorMessage);
      message.error(errorMessage);
    }

    setLoading(false);
  };

  const handleVerifyTwoFactor = async () => {
    if (!twoFactorCode) {
      setTwoFactorError("Bitte Login-Code eingeben.");
      return;
    }

    setLoading(true);
    setTwoFactorError("");
    try {
      const response = await axios.post(`${API_URL}/login/verify-2fa`, {
        email: twoFactorEmail,
        code: twoFactorCode,
      });

      if (response.data.token) {
        await completeLogin(response.data.token);
      } else {
        setTwoFactorError("Login fehlgeschlagen (kein Token erhalten).");
      }
    } catch (err) {
      const errorMessage = getErrorMessage(
        err,
        "Login-Code ungültig oder nicht mehr gültig."
      );
      setTwoFactorError(errorMessage);
      message.error(errorMessage);
    }

    setLoading(false);
  };

  const handleRequestReset = async () => {
    if (!resetEmail) {
      message.error("Bitte E-Mail-Adresse angeben.");
      return;
    }

    setPwResetLoading(true);
    try {
      await axios.post(`${API_URL}/forgot-password`, { email: resetEmail });
      setPwResetHint(true);
      message.success("Wenn die E-Mail existiert, wurde ein Code zum Zurücksetzen gesendet.");
      setPwResetStep(2);
    } catch {
      setPwResetHint(true);
      message.error("Fehler beim Anfordern. Bitte später erneut versuchen.");
    }

    setPwResetLoading(false);
  };

  const handleResetPassword = async () => {
    if (!pwResetCode || !pwResetNew || !pwResetNew2) {
      message.error("Bitte alle Felder ausfüllen.");
      return;
    }

    if (pwResetNew !== pwResetNew2) {
      message.error("Die Passwörter stimmen nicht überein.");
      return;
    }

    setPwResetLoading(true);
    try {
      await axios.post(`${API_URL}/reset-password`, {
        email: resetEmail,
        code: pwResetCode,
        newPassword: pwResetNew,
      });

      message.success("Passwort wurde erfolgreich zurückgesetzt! Bitte jetzt neu einloggen.");
      setPwResetVisible(false);
      setPwResetStep(1);
      setPwResetCode("");
      setPwResetNew("");
      setPwResetNew2("");
      setPwResetHint(false);
    } catch (err) {
      if (err.response && err.response.data) {
        message.error(typeof err.response.data === "string" ? err.response.data : "Zurücksetzen fehlgeschlagen.");
      } else {
        message.error("Zurücksetzen fehlgeschlagen.");
      }
    }

    setPwResetLoading(false);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: `linear-gradient(135deg, ${COLORS.pageBg} 0%, #FFFFFF 55%, ${COLORS.primaryLight} 100%)`,
        padding: 24,
      }}
    >
      <Card
        style={{
          width: 390,
          borderRadius: 18,
          border: `1px solid ${COLORS.border}`,
          boxShadow: "0 16px 40px rgba(31, 78, 87, 0.12)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div
            style={{
              width: 54,
              height: 54,
              borderRadius: 16,
              background: COLORS.primary,
              margin: "0 auto 14px",
            }}
          />
          <Title level={3} style={{ margin: 0, color: COLORS.text }}>
            Anmeldung
          </Title>
          <Text style={{ color: COLORS.muted }}>
            Willkommen zurück bei Smart Routing
          </Text>
        </div>

        {!twoFactorStep && (
          <Form layout="vertical" onFinish={onFinish}>
            {loginError && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message={loginError}
              />
            )}

            <Form.Item
              label="E-Mail"
              name="email"
              rules={[
                { required: true, message: "Bitte E-Mail eingeben" },
                { type: "email", message: "Bitte gültige E-Mail-Adresse eingeben" },
              ]}
            >
              <Input size="large" />
            </Form.Item>

            <Form.Item
              label="Passwort"
              name="password"
              rules={[{ required: true, message: "Bitte Passwort eingeben" }]}
            >
              <Input.Password size="large" />
            </Form.Item>

            <Button type="primary" htmlType="submit" block loading={loading} size="large">
              Einloggen
            </Button>

            <Button
              type="link"
              htmlType="button"
              block
              onClick={() => window.location.assign("/register")}
              style={{ marginTop: 10, color: COLORS.primary }}
            >
              Noch kein Konto? Jetzt registrieren
            </Button>

            <Button
              type="link"
              htmlType="button"
              block
              style={{ color: COLORS.primaryDark }}
              onClick={() => setPwResetVisible(true)}
            >
              Passwort vergessen?
            </Button>
          </Form>
        )}

        {twoFactorStep && (
          <Form layout="vertical" onFinish={handleVerifyTwoFactor}>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <div>
                  Login-Code wurde an <b>{twoFactorEmail}</b> gesendet.
                  <br />
                  Der Code ist 10 Minuten gültig.
                </div>
              }
            />

            {twoFactorError && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message={twoFactorError}
              />
            )}

            <Form.Item label="Login-Code">
              <Input
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.toUpperCase())}
                maxLength={8}
                autoFocus
                size="large"
              />
            </Form.Item>

            <Button type="primary" htmlType="submit" block loading={loading} size="large">
              Anmeldung abschließen
            </Button>

            <Button
              type="link"
              htmlType="button"
              block
              style={{ marginTop: 10, color: COLORS.primaryDark }}
              onClick={() => {
                setTwoFactorStep(false);
                setTwoFactorCode("");
                setTwoFactorError("");
                setLoginError("");
              }}
            >
              Zurück zur Anmeldung
            </Button>
          </Form>
        )}
      </Card>

      <Modal
        open={pwResetVisible}
        title="Passwort zurücksetzen"
        onCancel={() => {
          setPwResetVisible(false);
          setPwResetStep(1);
          setPwResetCode("");
          setPwResetNew("");
          setPwResetNew2("");
          setPwResetHint(false);
        }}
        footer={null}
      >
        {pwResetStep === 1 && (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 14 }}
              message={
                <div>
                  <b>Hinweis:</b> Die Zustellung der E-Mail kann bis zu <b>5–10 Minuten</b> dauern.
                  <br />
                  Bitte prüfe auch deinen <b>Spam-Ordner</b>.
                </div>
              }
            />

            <p>Gib deine E-Mail-Adresse ein. Du erhältst einen Code zum Zurücksetzen.</p>

            <Input
              placeholder="E-Mail"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              autoFocus
              size="large"
              style={{ marginBottom: 12 }}
            />

            <Button
              type="primary"
              block
              loading={pwResetLoading}
              onClick={handleRequestReset}
              disabled={!resetEmail}
              size="large"
            >
              Code anfordern
            </Button>
          </>
        )}

        {pwResetStep === 2 && (
          <>
            {pwResetHint && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 14 }}
                message={
                  <div>
                    Bitte prüfe dein E-Mail-Postfach.
                    <br />
                    Die Zustellung kann bis zu <b>5–10 Minuten</b> dauern.
                    <br />
                    Prüfe auch deinen <b>Spam-Ordner</b>.
                  </div>
                }
              />
            )}

            <Form layout="vertical" onFinish={handleResetPassword}>
              <Form.Item label="Code aus der E-Mail">
                <Input value={pwResetCode} onChange={(e) => setPwResetCode(e.target.value)} maxLength={8} autoFocus />
              </Form.Item>

              <Form.Item label="Neues Passwort">
                <Input.Password value={pwResetNew} onChange={(e) => setPwResetNew(e.target.value)} minLength={8} />
              </Form.Item>

              <Form.Item label="Neues Passwort wiederholen">
                <Input.Password value={pwResetNew2} onChange={(e) => setPwResetNew2(e.target.value)} minLength={8} />
              </Form.Item>

              <Button type="primary" block loading={pwResetLoading} onClick={handleResetPassword}>
                Passwort zurücksetzen
              </Button>

              <Button type="link" block style={{ marginTop: 8, color: COLORS.primary }} onClick={() => setPwResetStep(1)}>
                Zurück zur E-Mail-Eingabe
              </Button>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default LoginPage;
